import { getApiBaseUrl } from "./apiBase";
import { dispatchSessionExpired, getSession, getValidJwtToken, setJwtToken } from "./authSession";
import { authDebugLog, describeHeadersForDebug } from "./authDebug";
import { IS_EMBED_PAGE } from "./embedFrame";

export { getApiBaseUrl };

export type BackendAuthErrorCode =
  | "NOT_AUTHENTICATED"
  | "USER_NOT_FOUND"
  | "USER_INACTIVE"
  | "NO_ROLES_ASSIGNED"
  | "AUTHORIZATION_UNAVAILABLE"
  | "METHOD_NOT_ALLOWED"
  | "INVALID_BACKEND_RESPONSE"
  | "INTERNAL_SERVER_ERROR"
  | "NOT_FOUND";

export type BackendAuthUser = {
  id: string;
  email: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  isActive: boolean;
  roles: string[];
  role?: string | null;
  assignedRoles?: string[];
  primaryRole?: string | null;
  lastLoginAt?: string | null;
  tenantId?: string | null;
  tenant?: string | null;
  hosPermissions?: string[];
  properties?: string[];
  propertyIds?: string[];
  permissions?: string[];
  propertyPermissions?: Array<{ hotelId: string; permissions: string[] }>;
  fullAccess?: boolean;
  globalAdmin?: boolean;
  platformRole?: string | null;
};

type BackendAuthEnvelope = {
  ok: boolean;
  code?: BackendAuthErrorCode;
  error?: string;
  user?: BackendAuthUser;
  token?: string;
  session?: {
    cookieIssued?: boolean;
    verified?: boolean;
  };
};

const AUTH_FLASH_KEY = "xentros.auth.flash.v1";

// getApiBaseUrl lives in ./apiBase — a dependency-free module, so POS/CCTV
// files tested under Node can import it without dragging in this file's
// browser-only import chain.
function getApiUrl(path: string) {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) return path;
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

async function parseBackendAuthResponse(response: Response, requestLabel: string) {
  const contentType = response.headers.get("content-type");
  const responseText = await response.text().catch(() => "");
  let payload: BackendAuthEnvelope = { ok: response.ok };

  if (responseText && contentType?.includes("application/json")) {
    try {
      payload = JSON.parse(responseText) as BackendAuthEnvelope;
    } catch {
      payload = {
        ok: false,
        code: "INVALID_BACKEND_RESPONSE",
        error: `Backend returned invalid JSON for ${requestLabel}.`,
      };
    }
  } else if (responseText) {
    const looksLikeHtml = /^\s*<!doctype html>|^\s*<html/i.test(responseText);
    payload = {
      ok: false,
      code: looksLikeHtml ? "INVALID_BACKEND_RESPONSE" : (response.ok ? "INVALID_BACKEND_RESPONSE" : "INTERNAL_SERVER_ERROR"),
      error: looksLikeHtml
        ? `Backend returned HTML instead of JSON for ${requestLabel}. This usually means /api is not reaching the real backend.`
        : (responseText.trim() || `HTTP ${response.status}`),
    };
  }

  authDebugLog("[auth-debug]", {
    area: "backend-auth",
    event: "response",
    requestLabel,
    status: response.status,
    ok: response.ok,
    contentType,
    body: response.ok ? undefined : responseText,
  });

  return {
    ok: response.ok && payload.ok,
    status: response.status,
    code: payload.code,
    error: payload.error,
    user: payload.user,
    token: payload.token,
    session: payload.session,
  };
}

async function requestJson(path: string, init: RequestInit = {}, requestLabel = path) {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Content-Type") && init.body != null) {
    headers.set("Content-Type", "application/json");
  }

  const token = getValidJwtToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const url = getApiUrl(path);
  authDebugLog("[auth-debug]", {
    area: "backend-auth",
    event: "request",
    requestLabel,
    url,
    method: init.method ?? "GET",
    headers: describeHeadersForDebug(headers),
  });

  const response = await fetch(url, {
    ...init,
    headers,
    // The USM frame lives on its token alone. With the cookie, /api/auth/me or a logout
    // here would reach the normal session of a HOS tab in the same browser.
    credentials: IS_EMBED_PAGE ? "omit" : "include",
  });

  if (response.status === 401) {
    dispatchSessionExpired();
  }

  return parseBackendAuthResponse(response, requestLabel);
}

export async function getBackendAuthenticatedUser() {
  return requestJson("/api/auth/me", {}, "getBackendAuthenticatedUser");
}

// The JWT is kept in memory only (XEN-91), so a reload or a new tab starts
// without one. If this browser had a session, ask /api/auth/me to re-issue
// the JWT from the httpOnly session cookie.
export async function restoreJwtFromSessionCookie() {
  if (getValidJwtToken() || !getSession()) return;
  try {
    const result = await getBackendAuthenticatedUser();
    if (result.ok && result.token) setJwtToken(result.token);
  } catch {
    // Network failure: the caller falls through to the login screen.
  }
}

export async function logoutBackendSession() {
  return requestJson("/api/auth/logout", { method: "POST" }, "logoutBackendSession");
}

export function pushAuthFlashError(message: string) {
  sessionStorage.setItem(AUTH_FLASH_KEY, message);
}

export function pullAuthFlashError() {
  const value = sessionStorage.getItem(AUTH_FLASH_KEY);
  if (!value) return null;
  sessionStorage.removeItem(AUTH_FLASH_KEY);
  return value;
}

export function mapBackendErrorCodeToTranslationKey(code?: BackendAuthErrorCode) {
  switch (code) {
    case "USER_NOT_FOUND": return "auth.invalid";
    case "USER_INACTIVE": return "auth.account_inactive";
    case "NO_ROLES_ASSIGNED": return "auth.no_role_assigned_text";
    default: return null;
  }
}
